// Edition form for tournament card. `teamless` is a referee assigner's event:
// an outside organizer's schedule, so no registration terms and no image.
// `onCreate` creates it some other way: a schedule import, with its games,
// whose dry runs read the values as they are typed (`onValuesChange`)

import moment from 'moment';
import * as Yup from 'yup';
import { tournamentUpdate, tournamentCreate } from '../utils/firebase';
import Input from './Input';
import InputLocation from './InputLocation';
import InputMoney from './InputMoney';
import CardEditTemplate from './CardEditTemplate';
import InputDate from './InputDate';
import MultiSelect from './MultiSelect';
import Fieldset from './Fieldset';
import RadioButton from './RadioButton';
import Select from './Select';
import CheckBox from './CheckBox';
import { Sports, Genders, CutOffDates } from '../utils/enums';
import { ageLabels } from '../utils/agegroups';
import { getMatchDateTimeTz, switchGender } from '../utils/misc';
import {
  defaultTimeZone,
  tournamentPartisipationFee,
  tournamentPartisipationDeposit,
} from '../utils/consts';
import placeholderTournament from '../assets/images/placeholder-tournament.png';
import './TournamentCardEdit.scss';

// The US zones an event is played in
const timeZones = [
  'America/Los_Angeles',
  'America/Denver',
  'America/Phoenix',
  'America/Chicago',
  'America/New_York',
  'America/Anchorage',
  'Pacific/Honolulu',
];

// A hidden tournament may be an event nobody registers for
const registration = (schema) =>
  schema.when('hidden', { is: true, otherwise: (s) => s.min(1, 'Required') });

export default function TournamentCardEdit({
  tournament,
  teamless,
  afterCreateNavPath,
  onCreate = tournamentCreate,
  submitText,
  onCancel,
  onValuesChange,
}) {
  const {
    id,
    name,
    location,
    startDate,
    endDate,
    timezone,
    sports,
    ages,
    genders,
    imgUrl,
    imgThumbUrl,
    fee,
    deposit,
    cutOffDate,
    hidden,
  } = tournament;

  const event = {
    name: name || '',
    location: location || '',
    startDate: startDate?.toDate() || null,
    endDate: endDate?.toDate() || null,
  };
  const initialValues = teamless
    ? { ...event, timezone: timezone || defaultTimeZone }
    : {
        ...event,
        fee: hidden ? (fee ?? 0) : fee || tournamentPartisipationFee,
        deposit: deposit ?? tournamentPartisipationDeposit,
        sports: sports || [],
        ages: ages || [],
        genders: genders || [],
        cutOffDate: cutOffDate || CutOffDates.AUG_1,
        hidden: hidden === true,
      };

  const validationSchema = new Yup.ObjectSchema({
    name: Yup.string().max(100, 'Too long').required('Required'),
    location: Yup.string().max(200, 'Too long').required('Required'),
    startDate: Yup.date().required('Required'),
    endDate: Yup.date().required('Required'),
    ...(!teamless && {
      fee: Yup.number()
        .required('Required')
        .typeError('Must be a number')
        .when('hidden', {
          is: true,
          then: (s) => s.min(0, 'Cannot be negative'),
          otherwise: (s) => s.positive('Must be positive'),
        }),
      deposit: Yup.number()
        .required('Required')
        .typeError('Must be a number')
        .min(0, 'Cannot be negative'),
      sports: registration(Yup.array()),
      genders: registration(Yup.array()),
      ages: registration(Yup.array()),
    }),
  });

  const wrapArgsNormalize = (obj) => {
    const { startDate, endDate, fee, deposit, location } = obj;

    const tmpObj = { ...obj };

    if (fee) tmpObj.fee = +fee;
    if (deposit !== undefined && deposit !== '') tmpObj.deposit = +deposit;
    if (location) tmpObj.location = location.trim().replace(/\s{2,}/g, ' ');
    if (startDate)
      tmpObj.startDate = getMatchDateTimeTz(moment(startDate)).date;
    if (endDate) tmpObj.endDate = getMatchDateTimeTz(moment(endDate)).date;
    tmpObj.timezone ||= timezone || defaultTimeZone;

    return tmpObj;
  };

  return (
    <CardEditTemplate
      className='tournament-card-edit'
      id={id}
      imgUrl={imgUrl}
      imgThumbUrl={imgThumbUrl}
      placeholder={placeholderTournament}
      formConfig={{
        initialValues,
        validationSchema,
      }}
      create={(args) => onCreate(wrapArgsNormalize(args))}
      update={(args) => tournamentUpdate(wrapArgsNormalize(args))}
      afterCreateNavPath={afterCreateNavPath}
      withImage={!teamless}
      submitText={submitText}
      onCancel={onCancel}
      onValuesChange={
        onValuesChange &&
        ((values) => onValuesChange(wrapArgsNormalize(values)))
      }
    >
      {(formik) => (
        <>
          <div className='name-sports-fee'>
            <Input
              className='name'
              name='name'
              placeholder='Name'
              {...formik.getFieldProps('name')}
              error={
                formik.touched.name && formik.errors.name
                  ? formik.errors.name
                  : null
              }
            />
            {teamless ? (
              <Select
                className='timezone'
                options={timeZones}
                value={formik.values.timezone}
                onChange={(v) => formik.setFieldValue('timezone', v)}
                placeholder='Time zone'
              />
            ) : (
              <>
                <MultiSelect
                  options={Object.values(Sports).map((s) => ({
                    value: s,
                    label: s,
                  }))}
                  value={formik.values.sports}
                  onChange={async (newSports) => {
                    await formik.setFieldValue('sports', newSports);
                    await formik.setTouched({
                      ...formik.touched,
                      sports: true,
                    });
                  }}
                  className='sports'
                  placeholder='Sports'
                  error={
                    formik.touched.sports && formik.errors.sports
                      ? formik.errors.sports
                      : null
                  }
                />
                <div className='money'>
                  <InputMoney
                    className='fee'
                    name='fee'
                    placeholder='Fee'
                    value={formik.values.fee}
                    onChange={(value) => formik.setFieldValue('fee', value)}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.fee && formik.errors.fee
                        ? formik.errors.fee
                        : null
                    }
                  />
                  <InputMoney
                    className='deposit'
                    name='deposit'
                    placeholder='Deposit'
                    value={formik.values.deposit}
                    onChange={(value) => formik.setFieldValue('deposit', value)}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.deposit && formik.errors.deposit
                        ? formik.errors.deposit
                        : null
                    }
                  />
                </div>
              </>
            )}
          </div>
          <div className='location-dates'>
            <InputLocation
              className='location'
              level='address'
              value={formik.values.location}
              onChange={(val) => formik.setFieldValue('location', val)}
              onBlur={formik.handleBlur}
              error={
                formik.touched.location && formik.errors.location
                  ? formik.errors.location
                  : null
              }
            />
            <div className='dates'>
              <InputDate
                value={formik.values.startDate}
                onChange={async (newDate) => {
                  const endDate = formik.values.endDate;
                  await formik.setFieldValue('startDate', newDate);
                  await formik.setTouched({
                    ...formik.touched,
                    startDate: true,
                  });
                  if (endDate && newDate > endDate)
                    formik.setFieldValue('endDate', newDate);
                }}
                className='begin-date'
                minDate={id ? undefined : new Date()}
                placeholder='Begin at'
                error={
                  formik.touched.startDate && formik.errors.startDate
                    ? formik.errors.startDate
                    : null
                }
              />
              <InputDate
                value={formik.values.endDate}
                onChange={async (newDate) => {
                  const startDate = formik.values.startDate;
                  await formik.setFieldValue('endDate', newDate);
                  await formik.setTouched({ ...formik.touched, endDate: true });
                  if (startDate && startDate > newDate)
                    formik.setFieldValue('startDate', newDate);
                }}
                className='end-date'
                minDate={id ? undefined : new Date()}
                placeholder='End at'
                error={
                  formik.touched.endDate && formik.errors.endDate
                    ? formik.errors.endDate
                    : null
                }
              />
            </div>
          </div>
          {!teamless && (
            <div className='division-cut-off'>
              <MultiSelect
                options={Object.values(Genders).map((gender) => ({
                  value: gender,
                  label: switchGender(gender),
                }))}
                value={formik.values.genders}
                onChange={async (newGenders) => {
                  await formik.setFieldValue('genders', newGenders);
                  await formik.setTouched({ ...formik.touched, genders: true });
                }}
                className='genders'
                placeholder='Genders'
                error={
                  formik.touched.genders && formik.errors.genders
                    ? formik.errors.genders
                    : null
                }
              />
              <MultiSelect
                options={ageLabels.map((l) => ({ value: l, label: l }))}
                value={formik.values.ages}
                onChange={async (newAges) => {
                  await formik.setFieldValue('ages', newAges);
                  await formik.setTouched({ ...formik.touched, ages: true });
                }}
                className='ages'
                placeholder='Ages'
                error={
                  formik.touched.ages && formik.errors.ages
                    ? formik.errors.ages
                    : null
                }
              />
              <Fieldset label='Cut-off date' className='cut-off-date'>
                <RadioButton
                  label={CutOffDates.AUG_1}
                  name='cutOffDate'
                  value={CutOffDates.AUG_1}
                  selectedValue={formik.values.cutOffDate}
                  onChange={(val) => formik.setFieldValue('cutOffDate', val)}
                />
                <RadioButton
                  label={CutOffDates.JAN_1}
                  name='cutOffDate'
                  value={CutOffDates.JAN_1}
                  selectedValue={formik.values.cutOffDate}
                  onChange={(val) => formik.setFieldValue('cutOffDate', val)}
                />
              </Fieldset>
            </div>
          )}
          {!teamless && (
            <CheckBox
              className='hidden-check'
              label='Hidden: not on the public Home'
              value={formik.values.hidden}
              onChange={(v) => formik.setFieldValue('hidden', v)}
            />
          )}
        </>
      )}
    </CardEditTemplate>
  );
}
